'use client';

import { useState } from 'react';
import axios from 'axios';
import { Badge, Card, Switch, Text, Title } from '@tremor/react';
import { useQuery, useQueryClient } from 'react-query';
import { useToast } from '../../../utils/useToast';
import {
  CalendarOption,
  clearTeamsWebhook,
  Connection,
  connectionsQueryKey,
  disconnect,
  errorMessage,
  fetchCalendars,
  importWorkspace,
  ImportResult,
  Preview,
  previewWorkspace,
  Provider,
  setTeamsWebhook,
  startConnect,
  updateConnection,
  useConnections,
  useIntegrationCallbackToast,
} from './integrations/api';
import {
  CalendarConnections,
  ConnectionStatus,
} from './integrations/CalendarConnections';

const API_HOST = process.env.REACT_APP_API_HOST;

const integrationsQueryKey = 'workspaceIntegrations';

/** Workspace-level flags still kept in workspace settings (Teams, legacy Slack). */
type WorkspaceIntegrations = {
  slackConnected?: boolean;
  teamsConnected?: boolean;
};

const fetchWorkspaceIntegrations = async (): Promise<WorkspaceIntegrations> => {
  const response = await axios.get(`${API_HOST}/v1/workspace/integrations`);
  const result = response.data;
  if (result.status === 'GET_WORKSPACE_INTEGRATIONS_SUCCESS') {
    return result.data || {};
  }
  throw new Error(result.message || 'Could not load integrations settings.');
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const primaryButton =
  'rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60';
const secondaryButton =
  'rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60';

function Section({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      className="rounded-tremor-default border border-tremor-border p-4 dark:border-dark-tremor-border"
    >
      <h4 className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
        {title}
      </h4>
      <p className="mt-1 text-tremor-default leading-6 text-tremor-content dark:text-dark-tremor-content">
        {description}
      </p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function useConnectAction() {
  const { toast } = useToast();
  const [pending, setPending] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const connect = async (provider: Provider) => {
    setPending(`connect:${provider}`);
    try {
      await startConnect(provider, 'settings');
    } catch (err) {
      setPending(null);
      toast({
        title: 'Could not start the connection',
        description: errorMessage(err, 'Unable to reach the integration provider.'),
        variant: 'error',
      });
    }
  };

  const remove = async (connection: Connection, label: string) => {
    setPending(`disconnect:${connection.id}`);
    try {
      await disconnect(connection.id);
      toast({ title: 'Disconnected', description: `${label} is disconnected.`, variant: 'success' });
      await queryClient.invalidateQueries(connectionsQueryKey);
    } catch (err) {
      toast({ title: 'Disconnect failed', description: errorMessage(err, 'Try again.'), variant: 'error' });
    } finally {
      setPending(null);
    }
  };

  return { pending, connect, remove };
}

function TeamCalendarCard({ connections }: { connections: Connection[] }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const own = connections.filter(
    (c) => c.own && (c.provider === 'google-calendar' || c.provider === 'outlook-calendar'),
  );
  const current = connections.find((c) => c.settings.teamCalendar);
  const [connectionId, setConnectionId] = useState<string>(current?.own ? current.id : own[0]?.id || '');
  const [calendars, setCalendars] = useState<CalendarOption[] | null>(null);
  const [calendarId, setCalendarId] = useState<string>(current?.settings.teamCalendarId || '');
  const [hideLeaveTypes, setHideLeaveTypes] = useState<boolean>(current?.settings.hideLeaveTypes !== false);
  const [busy, setBusy] = useState(false);

  const loadCalendars = async (id: string) => {
    setConnectionId(id);
    setCalendars(null);
    if (!id) return;
    try {
      const list = await fetchCalendars(id);
      setCalendars(list);
      if (!list.some((c) => c.id === calendarId)) {
        setCalendarId(list.find((c) => c.primary)?.id || list[0]?.id || '');
      }
    } catch (err) {
      toast({ title: 'Could not load calendars', description: errorMessage(err, 'Try again.'), variant: 'error' });
    }
  };

  const save = async (enable: boolean) => {
    const id = enable ? connectionId : current?.id;
    if (!id) return;
    setBusy(true);
    try {
      await updateConnection(
        id,
        enable
          ? { teamCalendar: true, teamCalendarId: calendarId || null, hideLeaveTypes }
          : { teamCalendar: false },
      );
      toast({
        title: 'Saved',
        description: enable
          ? 'Approved leave for everyone will appear on the team calendar.'
          : 'The team calendar is turned off.',
        variant: 'success',
      });
      await queryClient.invalidateQueries(connectionsQueryKey);
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err, 'Try again.'), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      testId="team-calendar"
      title="Team calendar"
      description="Show everyone's approved leave on one shared calendar you own, as “Name — Leave type”."
    >
      {current && (
        <p className="mb-3 text-xs text-gray-500">
          Currently: {current.accountEmail || 'a connected calendar'}
          {current.status === 'needs_reconnect' ? ' (needs reconnect)' : ''}
        </p>
      )}
      {own.length === 0 ? (
        <p className="text-sm text-gray-500">Connect your Google or Outlook calendar above first.</p>
      ) : (
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-sm text-gray-700">
            Account
            <select
              aria-label="Team calendar account"
              className="rounded-md border-gray-300 text-sm"
              value={connectionId}
              onChange={(e) => loadCalendars(e.target.value)}
            >
              {own.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.accountEmail || c.provider}
                </option>
              ))}
            </select>
          </label>
          {calendars ? (
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              Calendar
              <select
                aria-label="Team calendar"
                className="rounded-md border-gray-300 text-sm"
                value={calendarId}
                onChange={(e) => setCalendarId(e.target.value)}
              >
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <button
              type="button"
              className="text-xs font-medium text-red-600 hover:underline"
              onClick={() => loadCalendars(connectionId)}
            >
              Choose a calendar
            </button>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Switch
              id="hideLeaveTypes"
              name="Hide leave types"
              checked={hideLeaveTypes}
              onChange={setHideLeaveTypes}
              color="rose"
            />
            <span>Hide leave types (show “Name — Away”)</span>
          </div>
          <div className="flex gap-2">
            <button type="button" className={primaryButton} disabled={busy || !connectionId} onClick={() => save(true)}>
              Use as team calendar
            </button>
            {current && (
              <button type="button" className={secondaryButton} disabled={busy} onClick={() => save(false)}>
                Turn off
              </button>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function WorkspaceCard({
  connection,
  available,
}: {
  connection?: Connection;
  available: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { pending, connect, remove } = useConnectAction();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [sendInvites, setSendInvites] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const connected = connection?.status === 'active';
  const lastSync = connection?.settings.lastSync;

  const runPreview = async () => {
    setBusy('preview');
    setResult(null);
    try {
      setPreview(await previewWorkspace());
    } catch (err) {
      toast({ title: 'Sync failed', description: errorMessage(err, 'Could not read the directory.'), variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const runImport = async () => {
    setBusy('import');
    try {
      const outcome = await importWorkspace({ sendInvites });
      setResult(outcome);
      setPreview(null);
      toast({
        title: 'Import finished',
        description: `${outcome.created} added, ${outcome.updated} updated${outcome.failed ? `, ${outcome.failed} failed` : ''}.`,
        variant: outcome.failed ? 'error' : 'success',
      });
      await queryClient.invalidateQueries(connectionsQueryKey);
    } catch (err) {
      toast({ title: 'Import failed', description: errorMessage(err, 'Try again.'), variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const counts = preview?.counts || {};

  return (
    <Section
      testId="google-workspace"
      title="Google Workspace"
      description="Import people from your Google Workspace directory (read-only). Matches by email; never changes roles or deactivates anyone."
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <ConnectionStatus connection={connection} />
          {connection?.accountEmail && <span>{connection.accountEmail}</span>}
          <span>Last sync: {formatTimestamp(lastSync?.at)}</span>
          {lastSync && (
            <span>
              ({lastSync.created} added, {lastSync.updated} updated, {lastSync.invited} invited)
            </span>
          )}
          {!available && <span>Not set up on this server yet.</span>}
        </div>
        <div className="flex shrink-0 gap-2">
          {(!connection || connection.status === 'needs_reconnect') && (
            <button
              type="button"
              className={primaryButton}
              onClick={() => connect('google-workspace')}
              disabled={pending !== null || !available}
            >
              {connection ? 'Reconnect' : 'Connect'}
            </button>
          )}
          {connection && (
            <button
              type="button"
              className={secondaryButton}
              onClick={() => remove(connection, 'Google Workspace')}
              disabled={pending !== null}
            >
              Disconnect
            </button>
          )}
          <button
            type="button"
            className={secondaryButton}
            onClick={runPreview}
            disabled={!connected || busy !== null}
          >
            {busy === 'preview' ? 'Reading directory…' : 'Sync now'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-4 space-y-3" data-testid="workspace-preview">
          <p className="text-sm text-gray-700">
            {counts.total ?? preview.users.length} people in the directory: {counts.create ?? 0} new,{' '}
            {counts.update ?? 0} to update, {counts.unchanged ?? 0} unchanged
            {counts.conflict ? `, ${counts.conflict} skipped (email used by another Redstone account)` : ''}.
          </p>
          <div className="max-h-64 overflow-auto rounded border border-gray-200">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-1">Name</th>
                  <th className="px-2 py-1">Email</th>
                  <th className="px-2 py-1">Job title</th>
                  <th className="px-2 py-1">Department</th>
                  <th className="px-2 py-1">Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.users.map((row) => (
                  <tr key={row.email} className="border-t border-gray-100">
                    <td className="px-2 py-1">
                      {row.firstName} {row.lastName}
                    </td>
                    <td className="px-2 py-1">{row.email}</td>
                    <td className="px-2 py-1">{row.jobTitle || '—'}</td>
                    <td className="px-2 py-1">{row.department || '—'}</td>
                    <td className="px-2 py-1">
                      {row.action === 'update' ? `update ${row.changes.join(', ')}` : row.action}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Switch
              id="sendInvites"
              name="Send invites"
              checked={sendInvites}
              onChange={setSendInvites}
              color="rose"
            />
            <span>
              Send invites (otherwise new people are added inactive, without an account or email)
            </span>
          </div>
          <button type="button" className={primaryButton} onClick={runImport} disabled={busy !== null}>
            {busy === 'import' ? 'Importing…' : 'Import'}
          </button>
        </div>
      )}

      {result && result.errors.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-xs text-red-600">
          {result.errors.slice(0, 10).map((e) => (
            <li key={e.email}>
              {e.email}: {e.message}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function SlackCard({
  connection,
  available,
  legacyWebhook,
}: {
  connection?: Connection;
  available: boolean;
  legacyWebhook: boolean;
}) {
  const { pending, connect, remove } = useConnectAction();

  return (
    <Section
      testId="slack"
      title="Slack"
      description="Posts leave requests and decisions to a channel, and DMs approvers and employees (matched by email)."
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <ConnectionStatus connection={connection} />
          {connection?.settings.slackTeamName && <span>{connection.settings.slackTeamName}</span>}
          {connection?.settings.slackChannel && <span>Channel: {connection.settings.slackChannel}</span>}
          {!connection && legacyWebhook && (
            <span>Using a pasted incoming webhook. Add the Slack app to also get direct messages.</span>
          )}
          {!available && <span>Not set up on this server yet.</span>}
        </div>
        <div className="flex shrink-0 gap-2">
          {(!connection || connection.status === 'needs_reconnect') && (
            <button
              type="button"
              className={primaryButton}
              onClick={() => connect('slack')}
              disabled={pending !== null || !available}
            >
              {connection ? 'Reinstall Slack app' : 'Add to Slack'}
            </button>
          )}
          {connection && (
            <button
              type="button"
              className={secondaryButton}
              onClick={() => remove(connection, 'Slack')}
              disabled={pending !== null}
            >
              Disconnect
            </button>
          )}
        </div>
      </div>
    </Section>
  );
}

function TeamsCard({ connected }: { connected: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action: 'save' | 'clear') => {
    setBusy(true);
    try {
      if (action === 'save') {
        await setTeamsWebhook(url.trim());
        setUrl('');
      } else {
        await clearTeamsWebhook();
      }
      toast({
        title: 'Saved',
        description: action === 'save' ? 'Microsoft Teams notifications are on.' : 'Microsoft Teams notifications are off.',
        variant: 'success',
      });
      await queryClient.invalidateQueries(integrationsQueryKey);
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err, 'Check the URL and try again.'), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      testId="teams"
      title="Microsoft Teams"
      description="Posts leave requests and decisions to a Teams channel through an incoming webhook (or a Workflows webhook URL)."
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <Badge color={connected ? 'green' : 'gray'}>{connected ? 'Connected' : 'Not connected'}</Badge>
        {connected && <span>A webhook URL is saved. It is not shown again.</span>}
      </div>
      <div className="mt-3 flex flex-col gap-2 md:flex-row">
        <input
          type="url"
          aria-label="Teams webhook URL"
          placeholder="https://….webhook.office.com/…"
          className="flex-1 rounded-md border-gray-300 text-sm"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button type="button" className={primaryButton} disabled={busy || !url.trim()} onClick={() => run('save')}>
          {connected ? 'Replace' : 'Save'}
        </button>
        {connected && (
          <button type="button" className={secondaryButton} disabled={busy} onClick={() => run('clear')}>
            Remove
          </button>
        )}
      </div>
    </Section>
  );
}

export function IntegrationsForm() {
  useIntegrationCallbackToast();
  const connectionsQuery = useConnections();
  const workspaceQuery = useQuery(integrationsQueryKey, fetchWorkspaceIntegrations, {
    staleTime: 5 * 60 * 1000,
  });

  if (connectionsQuery.isLoading || workspaceQuery.isLoading) {
    return (
      <Card className="mt-6">
        <Title>Integrations</Title>
        <Text>Loading integration settings...</Text>
      </Card>
    );
  }

  if (connectionsQuery.error || !connectionsQuery.data) {
    return (
      <Card className="mt-6">
        <Title>Integrations</Title>
        <Text>Error loading integration settings.</Text>
      </Card>
    );
  }

  const { connections, available } = connectionsQuery.data;
  const company = (provider: Provider) =>
    connections.find((c) => c.provider === provider && c.level === 'company');
  const workspace = workspaceQuery.data || {};

  return (
    <Card className="mt-6">
      <Title>Integrations</Title>
      <Text>Connect calendars, your directory and chat tools.</Text>

      <div className="mt-6 space-y-6">
        <Section
          testId="my-calendars"
          title="My calendars"
          description="Your own calendars. Everyone can connect theirs from their Profile → Calendar tab."
        >
          <CalendarConnections returnTo="settings" />
        </Section>

        <TeamCalendarCard connections={connections} />

        <WorkspaceCard
          connection={company('google-workspace')}
          available={available?.['google-workspace'] !== false}
        />

        <SlackCard
          connection={company('slack')}
          available={available?.slack !== false}
          legacyWebhook={Boolean(workspace.slackConnected)}
        />

        <TeamsCard connected={Boolean(workspace.teamsConnected)} />
      </div>
    </Card>
  );
}
